import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../Component/Sidebar';
import { fetchStoresWithNotesThunk } from '../features/stores/storeThunks';
import { isDeptHeadOrAbove } from '../RoleBasedRoute';
import '../CSS/StoreNotes.css';

const StoreNotesPage = () => {
  const dispatch = useDispatch();
  const { notesData } = useSelector(state => state.stores);
  const { user } = useSelector(state => state.auth);
  const isAdmin = isDeptHeadOrAbove(user);
  const [search, setSearch] = useState('');
  const [importantOnly, setImportantOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    dispatch(fetchStoresWithNotesThunk({ search: '', importantOnly }));
  }, [dispatch, importantOnly]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStoreToggle = (storeId) => {
    setSelectedStores(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStores.length === filteredStoresForDropdown.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(filteredStoresForDropdown.map(store => store.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedStores([]);
  };

  // Base list — admins can toggle inactive stores on/off
  const activeFilteredStores = notesData.storesWithNotes.filter(store =>
    isAdmin && includeInactive ? true : store.is_active !== false
  );

  // Filter stores for dropdown based on search
  const filteredStoresForDropdown = activeFilteredStores.filter(store =>
    store.store_name.toLowerCase().includes(search.toLowerCase())
  );

  // Filter stores for table display based on selection
  const displayedStores = selectedStores.length > 0
    ? activeFilteredStores.filter(store => selectedStores.includes(store.id))
    : activeFilteredStores;

  const getSelectedStoreNames = () => {
    if (selectedStores.length === 0) return 'Select stores...';
    if (selectedStores.length === 1) {
      const store = notesData.storesWithNotes.find(s => s.id === selectedStores[0]);
      return store ? store.store_name : 'Select stores...';
    }
    return `${selectedStores.length} stores selected`;
  };

  return (
    <div className="index-box">
      <Sidebar />
      <div className="store-notes-content">
        <div className="store-notes-header">
          <h1>Store Notes & Updates</h1>
          <p className="store-notes-subtitle">
            View important notes and updates for stores
          </p>
        </div>

        {/* Filters Section */}
        <div className="store-notes-filters-section">
          <div className="store-notes-filters-row">
            {/* Multi-Select Dropdown */}
            <div className="store-notes-multiselect-wrapper" ref={dropdownRef}>
              <div
                className="store-notes-multiselect-trigger"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="store-notes-multiselect-label">
                  {getSelectedStoreNames()}
                </span>
                <svg
                  className={`store-notes-dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isDropdownOpen && (
                <div className="store-notes-dropdown-panel">
                  {/* Search Input */}
                  <div className="store-notes-dropdown-search">
                    <input
                      type="text"
                      placeholder="Search stores..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="store-notes-dropdown-search-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Actions */}
                  <div className="store-notes-dropdown-actions">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="store-notes-dropdown-action-btn"
                    >
                      {selectedStores.length === filteredStoresForDropdown.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                    {selectedStores.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="store-notes-dropdown-action-btn clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Store List */}
                  <div className="store-notes-dropdown-list">
                    {filteredStoresForDropdown.length > 0 ? (
                      filteredStoresForDropdown.map((store) => (
                        <label
                          key={store.id}
                          className={`store-notes-dropdown-item${store.is_active === false ? ' inactive' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedStores.includes(store.id)}
                            onChange={() => handleStoreToggle(store.id)}
                            className="store-notes-dropdown-checkbox"
                          />
                          <div className="store-notes-dropdown-item-content">
                            <span className="store-notes-dropdown-item-name">
                              {store.store_name}
                            </span>
                            {store.is_active === false && isAdmin && (
                              <span className="store-notes-inactive-badge" title="This store is currently inactive">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="store-notes-dropdown-item-badges">
                            <span className="store-notes-dropdown-badge">
                              {store.notes_count}
                            </span>
                            {store.important_notes_count > 0 && (
                              <span className="store-notes-dropdown-badge important">
                                {store.important_notes_count} !
                              </span>
                            )}
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="store-notes-dropdown-empty">
                        No stores found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Important Only Filter */}
            <label className="store-notes-checkbox-label">
              <input
                type="checkbox"
                checked={importantOnly}
                onChange={(e) => setImportantOnly(e.target.checked)}
              />
              <span className="store-notes-checkbox-custom"></span>
              <span>Important notes only</span>
            </label>

            {/* Admin-only: Include inactive stores */}
            {isAdmin && (
              <label className="store-notes-checkbox-label" style={{ color: '#f59e0b' }}>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => {
                    setIncludeInactive(e.target.checked);
                    setSelectedStores([]); // reset selection when toggling
                  }}
                />
                <span className="store-notes-checkbox-custom"></span>
                <span>Include inactive stores</span>
              </label>
            )}
          </div>

          {/* Selected Count */}
          {selectedStores.length > 0 && (
            <div className="store-notes-selection-info">
              Showing {displayedStores.length} store(s) with {displayedStores.reduce((sum, store) => sum + store.notes_count, 0)} total notes
            </div>
          )}
        </div>

        {/* Table Section */}
        {notesData.loading ? (
          <div className="store-notes-loading">
            <div className="store-notes-loader"></div>
            <p>Loading stores...</p>
          </div>
        ) : displayedStores.length > 0 ? (
          <div className="store-notes-table-container">
            <table className="store-notes-table">
              <thead>
                <tr>
                  <th className="store-notes-th store-name-col">Store Name</th>
                  <th className="store-notes-th notes-col">Notes</th>
                </tr>
              </thead>
              <tbody>
                {displayedStores.map((store) => (
                  <tr key={store.id} className={`store-notes-tr${store.is_active === false ? ' store-notes-tr-inactive' : ''}`}>
                    <td className="store-notes-td">
                      <div className="store-notes-store-info">
                        {store.image && (
                          <img
                            src={store.image}
                            alt={store.store_name}
                            className="store-notes-store-image"
                          />
                        )}
                        <div className="store-notes-store-details">
                          <div className="store-notes-store-name">
                            {store.store_name}
                            {store.is_active === false && isAdmin && (
                              <span className="store-notes-inactive-badge" title="This store is currently inactive">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="store-notes-store-meta">
                            {store.notes_count} note{store.notes_count !== 1 ? 's' : ''}
                            {store.important_notes_count > 0 && (
                              <span className="store-notes-important-count">
                                • {store.important_notes_count} important
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="store-notes-td notes-cell">
                      <div className="store-notes-list">
                        {store.notes.map((note) => (
                          <div
                            key={note.id}
                            className={`store-note-item ${note.is_important ? 'important' : ''}`}
                          >
                            <div className="store-note-header">
                              {note.is_important && (
                                <span className="store-note-important-badge">
                                  Important
                                </span>
                              )}
                              <span className="store-note-date">
                                {new Date(note.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                            <p className="store-note-text">{note.note_text}</p>
                            <div className="store-note-footer">
                              <span className="store-note-author">
                                By {note.created_by_name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="store-notes-empty">
            <p>
              {selectedStores.length > 0
                ? 'No stores selected or no notes available for selected stores.'
                : importantOnly
                ? 'No stores with important notes found.'
                : 'No stores with notes yet.'}
            </p>
          </div>
        )}

        {notesData.error && (
          <div className="store-notes-error">
            {notesData.error}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreNotesPage;